/** All persistence lives in localStorage - the app works completely offline. */
import type { AppData, Card, VocabSet } from './types';
import { BOX_COUNT } from './types';

const STORAGE_KEY = 'voci.data.v1';
const CURRENT_VERSION = 1;

function emptyData(): AppData {
  return { version: CURRENT_VERSION, sets: [] };
}

function sanitizeCard(raw: unknown): Card | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const candidate = raw as Partial<Card>;
  if (typeof candidate.id !== 'string') return null;
  if (typeof candidate.native !== 'string' || typeof candidate.foreign !== 'string') return null;

  const box = Number(candidate.box);
  const asked = Number(candidate.asked);
  const correct = Number(candidate.correct);

  return {
    id: candidate.id,
    native: candidate.native,
    foreign: candidate.foreign,
    // Data written before learning goals existed has no group.
    group: typeof candidate.group === 'string' ? candidate.group : '',
    box: Number.isFinite(box) ? Math.min(Math.max(Math.round(box), 1), BOX_COUNT) : 1,
    asked: Number.isFinite(asked) && asked > 0 ? Math.round(asked) : 0,
    correct: Number.isFinite(correct) && correct > 0 ? Math.round(correct) : 0,
    lastAsked: typeof candidate.lastAsked === 'number' ? candidate.lastAsked : null,
  };
}

export function sanitizeSet(raw: unknown): VocabSet | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const candidate = raw as Partial<VocabSet>;
  if (typeof candidate.id !== 'string' || typeof candidate.name !== 'string') return null;
  if (!Array.isArray(candidate.cards)) return null;

  const cards = candidate.cards
    .map(sanitizeCard)
    .filter((card): card is Card => card !== null);

  const now = Date.now();
  return {
    id: candidate.id,
    name: candidate.name,
    nativeLang: typeof candidate.nativeLang === 'string' ? candidate.nativeLang : 'DE',
    foreignLang: typeof candidate.foreignLang === 'string' ? candidate.foreignLang : 'FR',
    createdAt: typeof candidate.createdAt === 'number' ? candidate.createdAt : now,
    updatedAt: typeof candidate.updatedAt === 'number' ? candidate.updatedAt : now,
    cards,
  };
}

export function loadData(): AppData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyData();

    const parsed = JSON.parse(raw) as Partial<AppData>;
    if (!Array.isArray(parsed.sets)) return emptyData();

    const sets = parsed.sets
      .map(sanitizeSet)
      .filter((set): set is VocabSet => set !== null);

    return { version: CURRENT_VERSION, sets };
  } catch (error) {
    console.error('Gespeicherte Daten konnten nicht gelesen werden.', error);
    return emptyData();
  }
}

export function saveData(data: AppData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Daten konnten nicht gespeichert werden.', error);
    alert('Der Speicher ist voll oder nicht verfügbar. Der Fortschritt wurde nicht gesichert.');
  }
}

export function getSets(): VocabSet[] {
  return loadData().sets;
}

export function getSet(id: string): VocabSet | null {
  return loadData().sets.find((set) => set.id === id) ?? null;
}

export function upsertSet(set: VocabSet): void {
  const data = loadData();
  const index = data.sets.findIndex((existing) => existing.id === set.id);
  const updated = { ...set, updatedAt: Date.now() };

  if (index === -1) {
    data.sets.push(updated);
  } else {
    data.sets[index] = updated;
  }

  saveData(data);
}

export function deleteSet(id: string): void {
  const data = loadData();
  data.sets = data.sets.filter((set) => set.id !== id);
  saveData(data);
}

/** Writes back the cards changed during a round without touching the rest of the set. */
export function updateCards(setId: string, changed: Card[]): void {
  if (changed.length === 0) return;

  const data = loadData();
  const set = data.sets.find((entry) => entry.id === setId);
  if (!set) return;

  const byId = new Map(changed.map((card) => [card.id, card]));
  set.cards = set.cards.map((card) => byId.get(card.id) ?? card);
  set.updatedAt = Date.now();

  saveData(data);
}

/** Resets all progress of a set back to box 1. */
export function resetProgress(setId: string): void {
  const data = loadData();
  const set = data.sets.find((entry) => entry.id === setId);
  if (!set) return;

  set.cards = set.cards.map((card) => ({
    ...card,
    box: 1,
    asked: 0,
    correct: 0,
    lastAsked: null,
  }));
  set.updatedAt = Date.now();

  saveData(data);
}

export const FIRST_RUN_KEY = 'voci.seeded.v1';
